/**
 * RoamerCore.ts — Engine #5: Roamer Core™, the heart of WorkRoamer.
 *
 * The central orchestrator. It does NOT replace engines — it coordinates them.
 * No engine calls another engine directly; everything flows through the core:
 *
 *   Engine publishes event → Roamer Core™ → interested engines are notified
 *
 * Responsibilities:
 *  • Event bus         — system-wide pub/sub (loose coupling)
 *  • Engine registry   — register/enable/disable/restart, versions, capabilities
 *  • Health monitoring — heartbeats, response times, restart-based recovery
 *  • Background tasks  — configurable scheduled jobs
 *  • System state      — single source of truth (base, online, theme, …)
 *  • Error management  — centralized logging, exceptions, metrics
 *  • Security          — centralized session/permission state
 *  • Observability     — developer diagnostics snapshot
 *
 * Workflow example (implemented below): LocationChanged → fusion refresh of
 * core domains in parallel → intelligence recalculation → "City is ready"
 * notification. Future engines just `registerEngine()` + subscribe.
 */

import type { RoamerLocation } from '@/services/RoamerLocationEngine';
import type { FusionDomain } from '@/services/fusion';
import { CoreEventBus } from './events/CoreEventBus';
import type { CoreEvent, CoreEventName } from './events/CoreEventBus';
import { EngineRegistry } from './registry/EngineRegistry';
import type { EngineAdapter, EngineInfo } from './registry/EngineRegistry';
import { HealthMonitor } from './health/HealthMonitor';
import { BackgroundScheduler } from './scheduler/BackgroundScheduler';
import type { BackgroundJob } from './scheduler/BackgroundScheduler';
import { SystemStateStore } from './state/SystemState';
import type { SystemStateSnapshot } from './state/SystemState';
import { CoreDiagnostics } from './diagnostics/CoreDiagnostics';
import { SecurityContext } from './security/SecurityContext';
import { resolveSecurityConfig, type SecurityResolution } from './security/securityConfig';
import {
  bridgeEngineEvents,
  buildBuiltinAdapters,
  defaultEngineDeps,
} from './adapters/builtinAdapters';
import type { CoreEngineDeps } from './adapters/builtinAdapters';

/** Domains refreshed (in parallel) when the current base changes. */
const BASE_CHANGE_DOMAINS: FusionDomain[] = [
  'weather', 'currency', 'housing', 'cafe', 'coworking', 'restaurant',
  'safety', 'events', 'communityReviews', 'internetSpeed', 'costOfLiving',
];

export interface CoreConfig {
  /** Auto-run the location-changed workflow when the base changes. */
  orchestrateLocationWorkflow: boolean;
  /** Start background job timers on initialize(). */
  startScheduler: boolean;
  /**
   * When true, every privileged control point (engine lifecycle operations,
   * orchestration workflows, background jobs) requires a valid session with
   * the matching permission — and FAILS CLOSED otherwise.
   *
   * BETA GATE HARDENING™: enabled by default in EVERY environment, resolved
   * from `resolveSecurityConfig()` — production always enforces; only an
   * explicit development env override (VITE_ENFORCE_SECURITY=false) may
   * disable it, and the boot log states the mode + configuration source.
   */
  enforceSecurity: boolean;
}

const DEFAULT_CONFIG: CoreConfig = {
  orchestrateLocationWorkflow: true,
  startScheduler: false, // future app bootstrap opts in; nothing runs unasked
  enforceSecurity: resolveSecurityConfig().enforceSecurity,
};

export interface CoreDiagnosticsSnapshot {
  engines: EngineInfo[];
  health: ReturnType<HealthMonitor['list']>;
  events: ReturnType<CoreEventBus['getStats']>;
  recentEvents: CoreEvent[];
  jobs: ReturnType<BackgroundScheduler['listJobs']>;
  state: SystemStateSnapshot;
  logs: ReturnType<CoreDiagnostics['getLogs']>;
  totalErrors: number;
}

export class RoamerCore {
  readonly bus: CoreEventBus;
  readonly registry: EngineRegistry;
  readonly healthMonitor: HealthMonitor;
  readonly scheduler: BackgroundScheduler;
  readonly state: SystemStateStore;
  readonly diagnostics: CoreDiagnostics;
  readonly security: SecurityContext;

  private deps: CoreEngineDeps;
  private config: CoreConfig;
  private initialized = false;
  private teardowns: Array<() => void> = [];

  constructor(deps?: CoreEngineDeps, config: Partial<CoreConfig> = {}) {
    this.deps = deps ?? defaultEngineDeps();
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.bus = new CoreEventBus();
    this.registry = new EngineRegistry();
    this.healthMonitor = new HealthMonitor(this.registry);
    this.scheduler = new BackgroundScheduler();
    this.state = new SystemStateStore();
    this.diagnostics = new CoreDiagnostics();
    this.security = new SecurityContext();
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  /**
   * Wire everything: register built-in engines, bridge their native events onto
   * the core bus, install orchestration workflows and default background jobs.
   * Idempotent — safe to call more than once.
   */
  initialize(now: number = Date.now()): void {
    if (this.initialized) return;
    this.initialized = true;

    // 1. Register built-in engines.
    for (const adapter of buildBuiltinAdapters(this.deps)) {
      this.registerEngine(adapter, now);
    }

    // 2. Bridge native engine events → core bus (engines stay untouched).
    this.teardowns.push(bridgeEngineEvents(this.deps, this.bus, () => Date.now()));

    // 3. Keep system state in sync with the core bus.
    this.teardowns.push(
      this.bus.on('LocationChanged', (event) => {
        const { location } = event.payload as { location: RoamerLocation };
        this.state.update({ currentBase: location }, event.at);
      }, 'core:state-sync'),
    );

    // 4. Orchestration workflow: base change → refresh → recalc → notify.
    if (this.config.orchestrateLocationWorkflow) {
      this.teardowns.push(
        this.bus.on('LocationChanged', (event) => {
          const { location } = event.payload as { location: RoamerLocation };
          void this.runLocationChangedWorkflow(location, event.at);
        }, 'core:location-workflow'),
      );
    }

    // 5. Connectivity → system state.
    this.teardowns.push(this.state.attachConnectivityListeners(() => Date.now()));

    // 6. Engine status → system state.
    for (const info of this.registry.list()) {
      this.state.setEngineStatus(info.id, info.status, now);
    }

    // 7. Default background jobs (timers only start when the scheduler does).
    this.installDefaultJobs();
    if (this.config.startScheduler) this.scheduler.start();

    // 8. Seed the current base.
    this.state.update({ currentBase: this.deps.location.getCurrentLocation() }, now);

    this.diagnostics.log('info', 'core', 'Roamer Core initialized', now);
  }

  /** Tear down subscriptions and timers (tests / hot reload). */
  shutdown(): void {
    this.teardowns.forEach((t) => t());
    this.teardowns = [];
    this.scheduler.stop();
    this.initialized = false;
  }

  // ── Security enforcement ─────────────────────────────────────────────────────

  /**
   * Central authorization gate for privileged core operations. With
   * `enforceSecurity` on, an invalid/expired session or a missing permission
   * denies the operation (fail closed) and is centrally logged.
   */
  private authorize(permission: string, now: number): boolean {
    if (!this.config.enforceSecurity) return true;
    const allowed = this.security.hasPermission(permission, now);
    if (!allowed) {
      this.authFailureCount += 1;
      this.lastAuthFailure = { permission, at: now };
      this.diagnostics.log('warn', 'security', `Denied "${permission}" (invalid session or missing permission)`, now);
    }
    return allowed;
  }

  /** BETA GATE HARDENING™ — authorization failure telemetry for Dev Center. */
  private authFailureCount = 0;
  private lastAuthFailure: { permission: string; at: number } | null = null;

  // ── Engine management (delegates + event publication) ───────────────────────

  registerEngine(adapter: EngineAdapter, now: number = Date.now()): void {
    if (!this.authorize('core.engines.manage', now)) return;
    this.registry.register(adapter, now);
    this.state.setEngineStatus(adapter.id, 'running', now);
    this.bus.publish('EngineRegistered', 'core', { engineId: adapter.id }, now);
  }

  unregisterEngine(id: string, now: number = Date.now()): boolean {
    if (!this.authorize('core.engines.manage', now)) return false;
    const removed = this.registry.unregister(id);
    if (removed) this.bus.publish('EngineStatusChanged', 'core', { engineId: id, status: 'unregistered' }, now);
    return removed;
  }

  enableEngine(id: string, now: number = Date.now()): boolean {
    if (!this.authorize('core.engines.manage', now)) return false;
    const ok = this.registry.enable(id);
    if (ok) {
      this.state.setEngineStatus(id, 'running', now);
      this.bus.publish('EngineStatusChanged', 'core', { engineId: id, status: 'running' }, now);
    }
    return ok;
  }

  disableEngine(id: string, now: number = Date.now()): boolean {
    if (!this.authorize('core.engines.manage', now)) return false;
    const ok = this.registry.disable(id);
    if (ok) {
      this.state.setEngineStatus(id, 'disabled', now);
      this.bus.publish('EngineStatusChanged', 'core', { engineId: id, status: 'disabled' }, now);
    }
    return ok;
  }

  async restartEngine(id: string, now: number = Date.now()): Promise<boolean> {
    if (!this.authorize('core.engines.manage', now)) return false;
    const ok = await this.registry.restart(id);
    const status = ok ? 'running' : 'failed';
    this.state.setEngineStatus(id, status, now);
    this.bus.publish('EngineStatusChanged', 'core', { engineId: id, status }, now);
    return ok;
  }

  /** Health sweep across all engines, with restart recovery + notifications. */
  async runHealthSweep(now: number = Date.now()): Promise<void> {
    const outcomes = await this.healthMonitor.checkAll(now);
    for (const outcome of outcomes) {
      const status = outcome.healthy ? 'running' : 'failed';
      this.state.setEngineStatus(outcome.engineId, status, now);
      if (outcome.recovered) {
        this.diagnostics.log('warn', 'health', `Engine "${outcome.engineId}" recovered after restart`, now);
      }
      if (outcome.needsAttention) {
        this.diagnostics.log('error', 'health', `Engine "${outcome.engineId}" failed and could not recover`, now);
        this.bus.publish('NotificationSent', 'core', {
          kind: 'engine-failure',
          message: `A background system (${outcome.engineId}) is temporarily unavailable.`,
        }, now);
      }
    }
  }

  // ── Orchestration workflows ──────────────────────────────────────────────────

  /**
   * The spec's flagship workflow. Current Base changes →
   * fusion refreshes every core domain IN PARALLEL → intelligence recalculates →
   * downstream engines are notified via events → optional "City is ready".
   */
  async runLocationChangedWorkflow(location: RoamerLocation, now: number = Date.now()): Promise<void> {
    if (!this.authorize('core.workflows.run', now)) return; // fail closed
    this.state.update({ syncStatus: 'syncing' }, now);
    try {
      await this.diagnostics.measure('workflow.locationChanged', now, async () => {
        // 1. Parallel data refresh through the Fusion Engine (Engine #4 handles
        //    caching, fallback and per-domain events itself).
        await Promise.all(
          BASE_CHANGE_DOMAINS.map((domain) =>
            this.deps.fusion.fetch(domain, location, { now }).catch((err) => {
              this.diagnostics.captureError(`fusion.${domain}`, err, now);
              return null; // one domain failing never blocks the workflow
            }),
          ),
        );

        // 2. Intelligence Engine recalculates for the new base. SMART cache
        //    invalidation: only the new base's entries are dropped (fusion's
        //    cache is per-location keyed and needs no clearing at all) —
        //    unrelated bases' data stays warm.
        this.deps.intelligence.clearCache(location);
        await this.deps.intelligence.getDailySuggestions({ now }).catch((err) => {
          this.diagnostics.captureError('intelligence.recalculate', err, now);
          return [];
        });

        // 3. Notify downstream engines/UI via events — never direct calls.
        this.bus.publish('RoamerScoreUpdated', 'core', { locationId: location.locationId }, now);
        this.bus.publish('JourneyUpdated', 'core', { locationId: location.locationId }, now);
        this.bus.publish('NotificationSent', 'core', {
          kind: 'base-ready',
          message: `${location.city} is ready.`,
        }, now);
      });
      this.state.update({ syncStatus: 'synced' }, now);
    } catch (err) {
      this.diagnostics.captureError('workflow.locationChanged', err, now);
      this.state.update({ syncStatus: 'error' }, now);
    } finally {
      this.bus.publish('WorkflowCompleted', 'core', { workflow: 'locationChanged', locationId: location.locationId }, now);
    }
  }

  // ── Background jobs ──────────────────────────────────────────────────────────

  private installDefaultJobs(): void {
    const MIN = 60_000;
    const refresh = (domain: FusionDomain) => async () => {
      if (!this.authorize('core.jobs.run', Date.now())) return; // fail closed
      const base = this.state.getSnapshot().currentBase;
      if (!base) return;
      await this.deps.fusion.fetch(domain, base, { bypassCache: true });
    };
    const jobs: BackgroundJob[] = [
      { id: 'refresh-weather', label: 'Refresh weather', intervalMs: 15 * MIN, run: refresh('weather') },
      { id: 'refresh-currency', label: 'Refresh currency', intervalMs: 15 * MIN, run: refresh('currency') },
      { id: 'refresh-housing', label: 'Update housing', intervalMs: 6 * 60 * MIN, run: refresh('housing') },
      { id: 'refresh-community', label: 'Refresh community', intervalMs: 5 * MIN, run: refresh('communityReviews') },
      { id: 'health-sweep', label: 'Engine health sweep', intervalMs: 5 * MIN, run: () => this.runHealthSweep() },
      { id: 'cache-cleanup', label: 'Cleanup expired cache', intervalMs: 60 * MIN, run: () => { this.deps.fusion.clearCache(); } },
    ];
    for (const job of jobs) this.scheduler.addJob(job);
  }

  // ── Convenience passthroughs ─────────────────────────────────────────────────

  publish<T>(name: CoreEventName, source: string, payload: T, now: number = Date.now()): void {
    this.bus.publish(name, source, payload, now);
  }

  on(name: CoreEventName, listener: (event: CoreEvent) => void, subscriberId?: string): () => void {
    return this.bus.on(name, listener, subscriberId);
  }

  /** Developer-only observability snapshot. */
  getDiagnostics(): CoreDiagnosticsSnapshot {
    return {
      engines: this.registry.list(),
      health: this.healthMonitor.list(),
      events: this.bus.getStats(),
      recentEvents: this.bus.getHistory().slice(-20),
      jobs: this.scheduler.listJobs(),
      state: this.state.getSnapshot(),
      logs: this.diagnostics.getLogs(),
      totalErrors: this.diagnostics.totalErrors,
    };
  }

  get isInitialized(): boolean {
    return this.initialized;
  }

  /** Security status for boot verification + Developer Center™ (read-only). */
  getSecurityInfo(now: number = Date.now()): SecurityResolution & {
    enforcedNow: boolean;
    hasValidSession: boolean;
    sessionUser: string | null;
    sessionPermissions: string[];
    authFailureCount: number;
    lastAuthFailure: { permission: string; at: number } | null;
  } {
    const resolution = resolveSecurityConfig();
    const session = this.security.getSession();
    return {
      ...resolution,
      enforcedNow: this.config.enforceSecurity,
      hasValidSession: this.security.isAuthenticated(now),
      sessionUser: session?.userId ?? null,
      sessionPermissions: session?.permissions ?? [],
      authFailureCount: this.authFailureCount,
      lastAuthFailure: this.lastAuthFailure ? { ...this.lastAuthFailure } : null,
    };
  }
}

/**
 * App-wide singleton. NOT auto-initialized — the future app bootstrap calls
 * `roamerCore.initialize()` once (backend-only sprint: no UI wiring here).
 */
export const roamerCore = new RoamerCore();
